import React from 'react';
import {
    preprocessTextForWrapping,
    splitText,
    getNonPrintableSiglature,
    getStandardCableSideSiglature
} from '../../utils/cableLogic';
import { renderTerminalSchematic } from '../../utils/terminalRenderLogic';
import { normalizeColorToCanonical } from '../../utils/colorUtils';
import CableSVGPath from './CableSVGPath';

const isEarthWire = (colore) => normalizeColorToCanonical(colore) === 'YE/GN';

const isValidSiglatura = (sig) => {
    if (!sig || String(sig).trim() === '') return false;
    const sigTrimmed = String(sig).trim();
    const invalidSigs = getNonPrintableSiglature();
    return !invalidSigs.includes(sigTrimmed);
};

const getSideValue = (cable, side, key) => cable?.[`${key}${side}`];

const getSideSiglatura = (cable, side) => {
    const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);
    return side === 'A' ? siglaturaA : siglaturaB;
};

const buildPolylinePath = (points = []) => points
    .filter((point) => point && Number.isFinite(point.x) && Number.isFinite(point.y))
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

const renderConnectionLabel = (text, x, y, textAnchor, lineX) => {
    if (!text) return null;

    return (
        <text
            x={x}
            y={y + 3}
            className="text-xs font-medium"
            textAnchor={textAnchor}
            fontSize="10"
            fill="#374151"
        >
            {splitText(preprocessTextForWrapping(text), 8).map((line, i) => (
                <tspan
                    key={i}
                    x={lineX}
                    dy={i === 0 ? 0 : 12}
                >
                    {line}
                </tspan>
            ))}
        </text>
    );
};

const renderSiglaturaBadge = (siglatura, x, y, align) => {
    if (!isValidSiglatura(siglatura)) return null;

    const width = (siglatura.length * 8) + 12;
    const rectX = align === 'left'
        ? x - 6
        : align === 'right'
            ? x - width + 6
            : x - (width / 2);
    const textAnchor = align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle';

    return (
        <g>
            <rect
                x={rectX}
                y={y - 8}
                width={width}
                height={16}
                rx={3}
                fill="white"
                stroke="#D1D5DB"
                strokeWidth={1}
                filter="drop-shadow(0 1px 3px rgba(0, 0, 0, 0.2))"
            />
            <text
                x={x}
                y={y + 4}
                className="text-xs font-bold fill-gray-800"
                textAnchor={textAnchor}
            >
                {siglatura}
            </text>
        </g>
    );
};

const renderIndexBadge = (index, x, y, opacity = 1) => (
    <g>
        <circle
            cx={x}
            cy={y}
            r={12}
            fill="#1F2937"
            opacity={0.95 * opacity}
        />
        <text
            x={x}
            y={y + 4}
            className="text-xs font-bold"
            fill="white"
            textAnchor="middle"
            opacity={opacity}
        >
            #{index}
        </text>
    </g>
);

const getSmartSiglaturaX = ({ centerX, terminalX, side, maxDistance = 30 }) => {
    if (side === 'A') {
        return Math.min(centerX, terminalX + maxDistance);
    }

    return Math.max(centerX, terminalX - maxDistance);
};

const countBranchNodes = (branchEntries = []) => branchEntries.reduce(
    (total, entry) => total + 1 + countBranchNodes(entry.children || []),
    0
);

const getOrderedBranchBuckets = (branchEntries = [], anchorIndex = 0) => {
    const aboveEntries = [];
    const belowEntries = [];

    branchEntries.forEach((entry) => {
        if ((entry?.cable?.originalIndex ?? 0) < anchorIndex) {
            aboveEntries.push(entry);
        } else {
            belowEntries.push(entry);
        }
    });

    return {
        aboveEntries: [...aboveEntries].sort((left, right) =>
            (right.cable?.originalIndex ?? 0) - (left.cable?.originalIndex ?? 0)
        ),
        belowEntries: [...belowEntries].sort((left, right) =>
            (left.cable?.originalIndex ?? 0) - (right.cable?.originalIndex ?? 0)
        )
    };
};

const sortPathEntriesForHover = (pathEntries = []) => (
    [...pathEntries].sort((left, right) => {
        const leftHovered = left.isHovered ? 1 : 0;
        const rightHovered = right.isHovered ? 1 : 0;
        return leftHovered - rightHovered;
    })
);

const flattenBranchRenderData = ({
    branchEntries,
    sharedSide,
    parentY,
    branchSpacing,
    joinX,
    sourceTerminalX,
    branchTerminalBaseX,
    horizontalDirection,
    rowDirection = 1,
    startOffset = 0
}) => {
    if (!branchEntries || branchEntries.length === 0) {
        return [];
    }

    let rowOffset = startOffset;
    const renderData = [];

    branchEntries.forEach((entry, branchIndex) => {
        const branchCable = entry.cable;
        const branchSide = entry.side;
        const displaySide = branchSide === 'A' ? 'B' : 'A';
        rowOffset += 1;

        const branchY = parentY + (rowOffset * branchSpacing * rowDirection);
        const branchHorizontalEndX = branchTerminalBaseX + (horizontalDirection * 18);

        renderData.push({
            key: `branch-${branchCable.originalIndex}-${sharedSide}-${branchIndex}-${rowOffset}`,
            cable: branchCable,
            sharedSide,
            branchY,
            branchJoinX: joinX,
            branchMidY: (parentY + branchY) / 2,
            branchHorizontalEndX,
            labelTerminal: getSideValue(branchCable, displaySide, 'terminale'),
            labelConnection: getSideValue(branchCable, displaySide, 'collegamento'),
            labelSiglatura: getSideSiglatura(branchCable, displaySide),
            pathD: buildPolylinePath([
                { x: sourceTerminalX, y: parentY },
                { x: joinX, y: parentY },
                { x: joinX, y: branchY },
                { x: branchHorizontalEndX, y: branchY }
            ])
        });

        const children = flattenBranchRenderData({
            branchEntries: entry.children || [],
            sharedSide,
            parentY: branchY,
            branchSpacing,
            joinX,
            sourceTerminalX: branchHorizontalEndX,
            branchTerminalBaseX: branchHorizontalEndX - (horizontalDirection * 18),
            horizontalDirection,
            rowDirection,
            startOffset: 0
        });

        renderData.push(...children);
        rowOffset += countBranchNodes(entry.children || []);
    });

    return renderData;
};

const SheathCableVisualization = ({
    cables,
    sheathLabel,
    hoveredCable,
    setHoveredCable,
    isMobile,
    settings,
    sheathType,
    cableBranches = {},
    showSheathBody = true
}) => {
    if (!cables || cables.length === 0) {
        return null;
    }

    const svgWidth = isMobile ? 400 : 800;
    const hasAnyBranches = cables.some((cable) => {
        const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
        return branches.A.length > 0 || branches.B.length > 0;
    });
    const visualRowsLeft = cables.reduce((total, cable) => {
        const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
        const { aboveEntries, belowEntries } = getOrderedBranchBuckets(branches.A, cable.originalIndex);
        return total + 1 + countBranchNodes(aboveEntries) + countBranchNodes(belowEntries);
    }, 0);
    const visualRowsRight = cables.reduce((total, cable) => {
        const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
        const { aboveEntries, belowEntries } = getOrderedBranchBuckets(branches.B, cable.originalIndex);
        return total + 1 + countBranchNodes(aboveEntries) + countBranchNodes(belowEntries);
    }, 0);
    const visualRows = Math.max(visualRowsLeft, visualRowsRight);
    const compactSvgHeight = Math.max(160, 40 + (cables.length * 50));
    const expandedSvgHeight = Math.max(160, 40 + (visualRows * 50));
    const svgHeight = hasAnyBranches ? expandedSvgHeight : compactSvgHeight;
    const mainCableY = svgHeight / 2;

    const sheathWidth = showSheathBody ? 300 : 0;
    const sheathHeight = 40;
    const sheathX = (svgWidth - sheathWidth) / 2;
    const sheathRect = {
        x: sheathX,
        y: mainCableY - sheathHeight / 2,
        width: sheathWidth,
        height: sheathHeight
    };

    const cableLeftX = 120;
    const cableRightX = svgWidth - 120;
    const compactCableSpacing = Math.min(50, (svgHeight - 40) / Math.max(1, cables.length - 1));
    const expandedCableSpacing = Math.min(50, (svgHeight - 40) / Math.max(1, visualRows - 1));
    const compactCableStartY = svgHeight / 2 - ((cables.length - 1) * compactCableSpacing) / 2;
    const cableStartYLeft = hasAnyBranches
        ? svgHeight / 2 - ((visualRowsLeft - 1) * expandedCableSpacing) / 2
        : compactCableStartY;
    const cableStartYRight = hasAnyBranches
        ? svgHeight / 2 - ((visualRowsRight - 1) * expandedCableSpacing) / 2
        : compactCableStartY;
    const isBraided = showSheathBody && sheathType === 'Trecciata';

    let currentRowLeft = 0;
    let currentRowRight = 0;

    const cableLayout = cables.map((cable, index) => {
        const branches = cableBranches[cable.originalIndex] || { A: [], B: [] };
        const leftBuckets = getOrderedBranchBuckets(branches.A, cable.originalIndex);
        const rightBuckets = getOrderedBranchBuckets(branches.B, cable.originalIndex);
        const leftAboveCount = countBranchNodes(leftBuckets.aboveEntries);
        const leftBelowCount = countBranchNodes(leftBuckets.belowEntries);
        const rightAboveCount = countBranchNodes(rightBuckets.aboveEntries);
        const rightBelowCount = countBranchNodes(rightBuckets.belowEntries);
        const cableYLeft = hasAnyBranches
            ? cableStartYLeft + ((currentRowLeft + leftAboveCount) * expandedCableSpacing)
            : compactCableStartY + (index * compactCableSpacing);
        const cableYRight = hasAnyBranches
            ? cableStartYRight + ((currentRowRight + rightAboveCount) * expandedCableSpacing)
            : compactCableStartY + (index * compactCableSpacing);

        if (hasAnyBranches) {
            currentRowLeft += 1 + leftAboveCount + leftBelowCount;
            currentRowRight += 1 + rightAboveCount + rightBelowCount;
        }

        return {
            cable,
            index,
            cableYLeft,
            cableYRight,
            branches,
            leftBuckets,
            rightBuckets
        };
    });

    return (
        <svg
            className="w-full"
            viewBox={`0 0 ${svgWidth} ${svgHeight}`}
            xmlns="http://www.w3.org/2000/svg"
        >
            <defs>
                <pattern id="braidPattern" patternUnits="userSpaceOnUse" width="30" height="30" patternTransform="rotate(45)">
                    <line x1="0" y1="0" x2="0" y2="30" stroke="white" strokeWidth="2" strokeOpacity="0.5" />
                    <line x1="0" y1="0" x2="30" y2="0" stroke="white" strokeWidth="2" strokeOpacity="0.5" />
                </pattern>
            </defs>

            {(() => {
                const lunghezzeUniche = [...new Set(cables.map(c => c.lunghezza || 0))].filter(l => l > 0).sort((a, b) => a - b);
                const maxLunghezza = lunghezzeUniche.length > 0 ? Math.max(...lunghezzeUniche) : 1;
                const maxScale = 80;
                const pathEntries = [];
                const decorationEntries = [];

                cableLayout.forEach(({ cable, index, cableYLeft, cableYRight, branches, leftBuckets, rightBuckets }) => {
                    const isHovered = hoveredCable === cable.originalIndex;

                    const cableExitXA = showSheathBody ? sheathRect.x : (svgWidth / 2) - 10;
                    const cableExitYA = showSheathBody
                        ? sheathRect.y + (sheathHeight / (cables.length + 1)) * (index + 1)
                        : cableYLeft;

                    const lunghezzaProporzionale = (cable.lunghezza || 0) / Math.max(maxLunghezza, 1);
                    const lunghezzaVisuale = lunghezzaProporzionale * maxScale;

                    const horizontalStartXA = cableLeftX + 40;
                    const horizontalEndXA = horizontalStartXA - lunghezzaVisuale;
                    const branchJoinXA = horizontalStartXA;

                    const cableExitXB = showSheathBody ? sheathRect.x + sheathRect.width : (svgWidth / 2) + 10;
                    const cableExitYB = showSheathBody
                        ? sheathRect.y + (sheathHeight / (cables.length + 1)) * (index + 1)
                        : cableYRight;

                    const horizontalStartXB = cableRightX - 40;
                    const horizontalEndXB = horizontalStartXB + lunghezzaVisuale;
                    const branchJoinXB = horizontalStartXB;
                    const branchRenderDataA = [
                        ...flattenBranchRenderData({
                        branchEntries: leftBuckets.aboveEntries,
                        sharedSide: 'A',
                        parentY: cableYLeft,
                        branchSpacing: expandedCableSpacing,
                        joinX: branchJoinXA,
                        sourceTerminalX: horizontalEndXA,
                        branchTerminalBaseX: horizontalEndXA,
                        horizontalDirection: 1,
                        rowDirection: -1
                    }),
                        ...flattenBranchRenderData({
                        branchEntries: leftBuckets.belowEntries,
                        sharedSide: 'A',
                        parentY: cableYLeft,
                        branchSpacing: expandedCableSpacing,
                        joinX: branchJoinXA,
                        sourceTerminalX: horizontalEndXA,
                        branchTerminalBaseX: horizontalEndXA,
                        horizontalDirection: 1,
                        rowDirection: 1
                    })
                    ];
                    const branchRenderDataB = [
                        ...flattenBranchRenderData({
                        branchEntries: rightBuckets.aboveEntries,
                        sharedSide: 'B',
                        parentY: cableYRight,
                        branchSpacing: expandedCableSpacing,
                        joinX: branchJoinXB,
                        sourceTerminalX: horizontalEndXB,
                        branchTerminalBaseX: horizontalEndXB,
                        horizontalDirection: -1,
                        rowDirection: -1
                    }),
                        ...flattenBranchRenderData({
                        branchEntries: rightBuckets.belowEntries,
                        sharedSide: 'B',
                        parentY: cableYRight,
                        branchSpacing: expandedCableSpacing,
                        joinX: branchJoinXB,
                        sourceTerminalX: horizontalEndXB,
                        branchTerminalBaseX: horizontalEndXB,
                        horizontalDirection: -1,
                        rowDirection: 1
                    })
                    ];

                    const { siglaturaA, siglaturaB } = getStandardCableSideSiglature(cable);

                    branchRenderDataA.forEach((branch) => {
                        const branchHovered = hoveredCable === branch.cable.originalIndex;
                        pathEntries.push({
                            key: branch.key,
                            cable: branch.cable,
                            d: branch.pathD,
                            isHovered: branchHovered,
                            opacity: branchHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: branch.cable.originalIndex
                        });
                    });

                    branchRenderDataB.forEach((branch) => {
                        const branchHovered = hoveredCable === branch.cable.originalIndex;
                        pathEntries.push({
                            key: branch.key,
                            cable: branch.cable,
                            d: branch.pathD,
                            isHovered: branchHovered,
                            opacity: branchHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: branch.cable.originalIndex
                        });
                    });

                    const mainPathD = showSheathBody
                        ? buildPolylinePath([
                            { x: cableExitXA, y: cableExitYA },
                            { x: horizontalStartXA, y: cableYLeft },
                            { x: horizontalEndXA, y: cableYLeft }
                        ])
                        : buildPolylinePath([
                            { x: horizontalEndXA, y: cableYLeft },
                            { x: horizontalStartXA, y: cableYLeft },
                            { x: horizontalStartXB, y: cableYRight },
                            { x: horizontalEndXB, y: cableYRight }
                        ]);

                    pathEntries.push({
                        key: `sheath-main-a-${cable.originalIndex}`,
                        cable,
                        d: mainPathD,
                        isHovered,
                        opacity: isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                        hoverIndex: cable.originalIndex
                    });

                    if (showSheathBody) {
                        pathEntries.push({
                            key: `sheath-main-b-${cable.originalIndex}`,
                            cable,
                            d: buildPolylinePath([
                                { x: cableExitXB, y: cableExitYB },
                                { x: horizontalStartXB, y: cableYRight },
                                { x: horizontalEndXB, y: cableYRight }
                            ]),
                            isHovered,
                            opacity: isHovered ? 1 : (hoveredCable !== null ? 0.3 : 1),
                            hoverIndex: cable.originalIndex
                        });
                    }

                    decorationEntries.push(
                        <g
                            key={`sheath-decor-a-${cable.originalIndex}`}
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {renderTerminalSchematic(
                                cable.terminaleA,
                                horizontalEndXA,
                                cableYLeft,
                                isMobile ? 0.03 : 0.04,
                                'A',
                                180,
                                settings.terminal_data
                            )}

                            {cable.terminaleA && cable.terminaleA !== 'NS' && (
                                <text
                                    x={horizontalEndXA}
                                    y={cableYLeft + 20}
                                    className="text-xs font-medium fill-gray-700"
                                    textAnchor="middle"
                                >
                                    {cable.terminaleA}
                                </text>
                            )}

                            {!isEarthWire(cable.colore) && renderSiglaturaBadge(
                                siglaturaA,
                                getSmartSiglaturaX({
                                    centerX: (horizontalStartXA + horizontalEndXA) / 2,
                                    terminalX: horizontalEndXA,
                                    side: 'A'
                                }),
                                cableYLeft,
                                'center'
                            )}

                            {cable.collegamentoA && renderConnectionLabel(
                                cable.collegamentoA,
                                horizontalEndXA - 30,
                                cableYLeft,
                                'end',
                                horizontalEndXA - 35
                            )}
                        </g>
                    );

                    decorationEntries.push(
                        <g
                            key={`sheath-decor-b-${cable.originalIndex}`}
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            {renderTerminalSchematic(
                                cable.terminaleB,
                                horizontalEndXB,
                                cableYRight,
                                isMobile ? 0.03 : 0.04,
                                'B',
                                0,
                                settings.terminal_data
                            )}

                            {cable.terminaleB && cable.terminaleB !== 'NS' && (
                                <text
                                    x={horizontalEndXB}
                                    y={cableYRight + 20}
                                    className="text-xs font-medium fill-gray-700"
                                    textAnchor="middle"
                                >
                                    {cable.terminaleB}
                                </text>
                            )}

                            {!isEarthWire(cable.colore) && renderSiglaturaBadge(
                                siglaturaB,
                                getSmartSiglaturaX({
                                    centerX: (horizontalStartXB + horizontalEndXB) / 2,
                                    terminalX: horizontalEndXB,
                                    side: 'B'
                                }),
                                cableYRight,
                                'center'
                            )}

                            {cable.collegamentoB && renderConnectionLabel(
                                cable.collegamentoB,
                                horizontalEndXB + 30,
                                cableYRight,
                                'start',
                                horizontalEndXB + 35
                            )}
                        </g>
                    );

                    branchRenderDataA.forEach((branch) => {
                        const branchOpacity = hoveredCable !== null && hoveredCable !== branch.cable.originalIndex ? 0.3 : 1;
                        decorationEntries.push(
                            <g
                                key={`${branch.key}-decor`}
                                onMouseEnter={() => setHoveredCable(branch.cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {renderIndexBadge(
                                    (branch.cable.originalIndex ?? 0) + 1,
                                    branch.branchJoinX - 20,
                                    branch.branchMidY,
                                    branchOpacity
                                )}
                                {renderTerminalSchematic(
                                    branch.labelTerminal,
                                    branch.branchHorizontalEndX,
                                    branch.branchY,
                                    isMobile ? 0.03 : 0.04,
                                    branch.sharedSide,
                                    branch.sharedSide === 'A' ? 180 : 0,
                                    settings.terminal_data
                                )}
                                {branch.labelTerminal && branch.labelTerminal !== 'NS' && (
                                    <text
                                        x={branch.branchHorizontalEndX}
                                        y={branch.branchY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {branch.labelTerminal}
                                    </text>
                                )}
                                {!isEarthWire(branch.cable.colore) && renderSiglaturaBadge(
                                    branch.labelSiglatura,
                                    getSmartSiglaturaX({
                                        centerX: (branch.branchJoinX + branch.branchHorizontalEndX) / 2,
                                        terminalX: branch.branchHorizontalEndX,
                                        side: 'A'
                                    }),
                                    branch.branchY,
                                    'center'
                                )}
                                {branch.labelConnection && renderConnectionLabel(
                                    branch.labelConnection,
                                    branch.branchHorizontalEndX - 30,
                                    branch.branchY,
                                    'end',
                                    branch.branchHorizontalEndX - 35
                                )}
                            </g>
                        );
                    });

                    branchRenderDataB.forEach((branch) => {
                        const branchOpacity = hoveredCable !== null && hoveredCable !== branch.cable.originalIndex ? 0.3 : 1;
                        decorationEntries.push(
                            <g
                                key={`${branch.key}-decor`}
                                onMouseEnter={() => setHoveredCable(branch.cable.originalIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                {renderIndexBadge(
                                    (branch.cable.originalIndex ?? 0) + 1,
                                    branch.branchJoinX + 20,
                                    branch.branchMidY,
                                    branchOpacity
                                )}
                                {renderTerminalSchematic(
                                    branch.labelTerminal,
                                    branch.branchHorizontalEndX,
                                    branch.branchY,
                                    isMobile ? 0.03 : 0.04,
                                    branch.sharedSide,
                                    branch.sharedSide === 'A' ? 180 : 0,
                                    settings.terminal_data
                                )}
                                {branch.labelTerminal && branch.labelTerminal !== 'NS' && (
                                    <text
                                        x={branch.branchHorizontalEndX}
                                        y={branch.branchY + 20}
                                        className="text-xs font-medium fill-gray-700"
                                        textAnchor="middle"
                                    >
                                        {branch.labelTerminal}
                                    </text>
                                )}
                                {!isEarthWire(branch.cable.colore) && renderSiglaturaBadge(
                                    branch.labelSiglatura,
                                    getSmartSiglaturaX({
                                        centerX: (branch.branchJoinX + branch.branchHorizontalEndX) / 2,
                                        terminalX: branch.branchHorizontalEndX,
                                        side: 'B'
                                    }),
                                    branch.branchY,
                                    'center'
                                )}
                                {branch.labelConnection && renderConnectionLabel(
                                    branch.labelConnection,
                                    branch.branchHorizontalEndX + 30,
                                    branch.branchY,
                                    'start',
                                    branch.branchHorizontalEndX + 35
                                )}
                            </g>
                        );
                    });
                });

                return (
                    <>
                        {sortPathEntriesForHover(pathEntries).map((entry) => (
                            <g
                                key={entry.key}
                                onMouseEnter={() => setHoveredCable(entry.hoverIndex)}
                                onMouseLeave={() => setHoveredCable(null)}
                                style={{ cursor: 'pointer' }}
                            >
                                <CableSVGPath
                                    cable={entry.cable}
                                    d={entry.d}
                                    isHovered={entry.isHovered}
                                    useAWG={settings.useAWG}
                                    strokeLinecap="round"
                                    opacity={entry.opacity}
                                />
                            </g>
                        ))}

                        {decorationEntries}
                    </>
                );
            })()}

            {showSheathBody && (
                <g>
                    <rect
                        x={sheathRect.x}
                        y={sheathRect.y}
                        width={sheathRect.width}
                        height={sheathRect.height}
                        fill="#1f2937"
                        stroke="#111827"
                        strokeWidth="2"
                        rx="5"
                    />

                    {isBraided && (
                        <rect
                            x={sheathRect.x}
                            y={sheathRect.y}
                            width={sheathRect.width}
                            height={sheathRect.height}
                            fill="url(#braidPattern)"
                            pointerEvents="none"
                            rx="5"
                            opacity={0.8}
                        />
                    )}
                </g>
            )}

            {showSheathBody && sheathLabel && (
                <text
                    x={sheathRect.x + sheathRect.width / 2}
                    y={sheathRect.y + sheathRect.height / 2 + 5}
                    textAnchor="middle"
                    fontSize="14"
                    fontWeight="600"
                    fill="white"
                >
                    {sheathLabel}
                </text>
            )}

            {showSheathBody && (() => {
                const indexSpacing = 40;
                const centerX = sheathRect.x + sheathRect.width / 2;
                const splitIndex = Math.ceil(cables.length / 2);

                return cables.map((cable, index) => {
                    const badgeOpacity = hoveredCable !== null && hoveredCable !== cable.originalIndex ? 0.3 : 1;
                    const isTop = index < splitIndex;
                    const subGroupIndex = isTop ? index : (index - splitIndex);
                    const subGroupLength = isTop ? splitIndex : (cables.length - splitIndex);
                    const startX = centerX - ((subGroupLength - 1) * indexSpacing) / 2;
                    const indexX = startX + (subGroupIndex * indexSpacing);
                    const indexY = isTop
                        ? sheathRect.y - 25
                        : sheathRect.y + sheathRect.height + 25;

                    return (
                        <g
                            key={`index-${cable.originalIndex}`}
                            onMouseEnter={() => setHoveredCable(cable.originalIndex)}
                            onMouseLeave={() => setHoveredCable(null)}
                            style={{ cursor: 'pointer' }}
                        >
                            <circle
                                cx={indexX}
                                cy={indexY}
                                r={15}
                                fill="#1F2937"
                                opacity={0.9 * badgeOpacity}
                            />
                            <text
                                className="cable-number"
                                x={indexX}
                                y={indexY + 5}
                                fontSize={14}
                                fill="white"
                                textAnchor="middle"
                                opacity={badgeOpacity}
                            >
                                #{cable.originalIndex + 1}
                            </text>
                        </g>
                    );
                });
            })()}
        </svg>
    );
};

export default SheathCableVisualization;
